const HTML_CHARACTER_SLASH = "&#x2F";
const HTML_CHARACTER_ZERO_WIDTH_SPACE = "&#x200b";
const FIND_SLASH_REGEX = new RegExp(HTML_CHARACTER_SLASH, "g");

export function htmlEscape(str?: string): string {
	return str ? str
			.replace(/&/g, "&amp;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/\//g, "&#x2F;")
		: str;
}

/**
	Checks if a string is not empty (""), not null and not whitespace only.
		stringNotBlank(null)      = false
		stringNotBlank("")        = false
		stringNotBlank(" ")       = false
		stringNotBlank("bob")     = true
		stringNotBlank("  bob  ") = true
 */
export function stringNotBlank(s: string | null | undefined) {
	return s != null && s.trim() !== "";
}

/**
 * Turns a given camel case string to kebab case.
 * Examples:
 *		input 	  			-> input
 * 		camelCase 			-> camel-case
 *		aLongInputString	-> a-long-input-string
 * @param input the camel case input string that should be converted to kebab case
 */
export function camelCaseToKebabCase(input: string): string {
	if (!input) {
		return input;
	}

	return input.split("").map((letter, idx) => {
		return letter.toUpperCase() === letter ? `${idx !== 0 ? "-" : ""}${letter.toLowerCase()}` : letter;
	}).join("");
}

/**
 * Checks whether a given input contains at least one character of a character pool string.
 *
 * Example:
 * 	input: "hello", characterPool: "aeiou", result: true
 * 	input: "hello", characterPool: "xyz", result: false
 *
 * @param input
 * @param characterPool
 */
export function containsAtLeastOneOf(input: string, characterPool: string): boolean {
	if (!input) {
		return false;
	}

	return input.split("").some(letter => characterPool.indexOf(letter) >= 0);
}

export function breadcrumbsWithoutName(breadcrumbs: string, name: string) {
	let result = breadcrumbs.substring(0, breadcrumbs.length - name.length);
	result = htmlEscape(result);
	// adds zero-width-space-character to allow breaks between task levels
	return result.replace(FIND_SLASH_REGEX, HTML_CHARACTER_SLASH + HTML_CHARACTER_ZERO_WIDTH_SPACE);
}

/**
 * Produces a URL-safe slug from a given text.
 * @param text
 */
export function slugifyText(text: string) {
	return text.replace(/[^a-zA-Z0-9/+]/g, "-") // Replace all non-alphanumeric characters except / with -
		.replace(/^(?:-+|\s+)/g, "") // Trim - and whitespace from the beginning of the line
		.replace(/(?:-+|\s+)$/g, "") // Trim - and whitespace from the end of the line
}

/**
 * Checks a string for non-printable ASCII characters
 * @param input the string that should be validated (can be from User input or scanner QR code etc.)
 * @private
 */
export function containsNonPrintableASCIIChars(input: string): boolean {
	for (let i = 0; i < input.length; i++) {
		const code = input.charCodeAt(i);
		if (code < 32 || code > 126) {
			return true;
		}
	}
	return false;
}

/**
 * Base64 encodes a sanitized string (passing containsNonPrintableASCIIChars method)
 * @param sanitizedInputString containing a string with only printable characters
 * @see
 */
export function encodeBase64(sanitizedInputString: string): string {
	const utf8Bytes = new TextEncoder().encode(sanitizedInputString);
	let binary = '';
	utf8Bytes.forEach(byte => binary += String.fromCharCode(byte));
	return btoa(binary);
}

export function isStringEnumType<T extends globalThis.Record<string, string>>(value: unknown, enumType: T): value is T[keyof T] {
	return typeof value === "string" && Object.values(enumType).includes(value);
}
